import React from "react";
import Typography from "../../../common/Typography";
import MarkdownWrapper from "../../../common/MarkdownWrapper";
import Button from "../../../common/Button";
import { CheckCircleIcon, InfoIcon } from "lucide-react";
import CryIcon from "../../../../images/CryIcon";

interface FeedbackMessageProps {
  isSuccess: boolean;
  title: string;
  message: string;
  showTryAgain?: boolean;
  showWhyButton?: boolean;
  showSeeAnswer?: boolean;
  onTryAgain?: () => void;
  onWhyClick?: () => void;
  onSeeAnswer?: () => void;
  tryAgainText?: string;
  whyButtonText?: string;
  seeAnswerText?: string;
  className?: string;
  customSuccessIcon?: React.ReactNode;
  customErrorIcon?: React.ReactNode;
  customInfoIcon?: React.ReactNode;
}

const FeedbackMessage: React.FC<FeedbackMessageProps> = ({
  isSuccess,
  title = "Oops, Close Call!",
  message = "The answer is incorrect. Try again.",
  showTryAgain = false,
  showWhyButton = false,
  showSeeAnswer = false,
  onTryAgain,
  onWhyClick,
  onSeeAnswer,
  tryAgainText = "Try Again",
  whyButtonText = "Why?",
  seeAnswerText = "See answer",
  className = "",
  customSuccessIcon,
  customErrorIcon,
  customInfoIcon,
}) => {
  // Success state: clean banner matching MatchExercise design
  if (isSuccess) {
    return (
      <div
        className={`mt-6 p-6 rounded-2xl border shadow-lg bg-[#D4EDDA] border-[#C3E6CB] mb-4 ${className}`.trim()}
      >
        <div className="flex items-center gap-3">
          {customSuccessIcon || (
            <div className="p-2 rounded-full bg-[#155724]">
              <CheckCircleIcon className="w-6 h-6 text-white" />
            </div>
          )}
          <div>
            <Typography
              variant="h6"
              weight="bold"
              className="mb-1 !text-[#155724]"
            >
              {title} 🎉
            </Typography>
            <div className="!text-[#155724]">
              <MarkdownWrapper content={message} className="text-sm" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state: existing design with action buttons
  return (
    <div
      className={`flex flex-col justify-start lg:flex-row lg:justify-between mt-6 p-4 rounded-lg border mb-4 bg-[#FFB3BA] border-[#F5A1AA] ${className}`.trim()}
    >
      <div>
        <div className="flex items-center gap-3 mb-4">
          {customErrorIcon || (
            <CryIcon className="max-sm:!w-16 max-sm:!h-16" />
          )}
          <Typography
            variant="h6"
            weight="bold"
            className="!text-[#721C24] max-sm:!text-base"
          >
            {title}
          </Typography>
        </div>

        <div className="mb-4 !text-[#721C24]">
          <MarkdownWrapper content={message} className="text-sm" />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-start lg:justify-end gap-4">
        {showTryAgain && onTryAgain && (
          <Button
            variant="danger"
            size="large"
            onClick={onTryAgain}
            className="w-full sm:w-36 h-12"
          >
            {tryAgainText}
          </Button>
        )}

        {showSeeAnswer && onSeeAnswer && (
          <Button
            size="large"
            variant="text"
            onClick={onSeeAnswer}
            className="w-full sm:w-36 h-12 !text-p text-textPrimary bg-inner-yellow"
          >
            {seeAnswerText}
          </Button>
        )}

        {showWhyButton && onWhyClick && (
          <Button
            size="large"
            variant="text"
            onClick={onWhyClick}
            className="w-full sm:w-[120px] h-11 !text-p text-textPrimary bg-inner-yellow"
          >
            {whyButtonText}
          </Button>
        )}

        {(showWhyButton || showSeeAnswer) &&
          (customInfoIcon || <InfoIcon className="w-6 h-6 sm:block hidden" />)}
      </div>
    </div>
  );
};

export default FeedbackMessage;
